/**
 * Centralized LOINC Codes for Wearable/Vital Observations
 *
 * This file serves as the single source of truth for all LOINC codes used
 * in the wearables data pipelines. All VitalObservation subclasses should
 * reference these codes instead of hardcoding strings.
 *
 * LOINC (Logical Observation Identifiers Names and Codes) is a universal
 * code system for identifying health measurements, observations, and documents.
 *
 * Verification Sources:
 * - LOINC Official Database: https://loinc.org/
 * - HL7 FHIR Specification: https://hl7.org/fhir/
 * - Stanford HealthKitOnFHIR: https://github.com/StanfordBDHG/HealthKitOnFHIR
 *   (Authoritative mappings for Apple HealthKit → FHIR LOINC codes)
 * - UCUM (Units): https://ucum.org/ucum
 *
 * Last verified: December 2024
 */

import type { CodeableConcept } from "@medplum/fhirtypes";

//------------------------------------------------------------------------------
// Observation Categories
//------------------------------------------------------------------------------

/**
 * FHIR Observation category codes used for vital observations.
 * @see https://www.hl7.org/fhir/valueset-observation-category.html
 */
export const ObservationCategories = {
  VitalSigns: {
    coding: [
      {
        system: "http://terminology.hl7.org/CodeSystem/observation-category",
        code: "vital-signs",
        display: "Vital Signs",
      },
    ],
  } as CodeableConcept,

  Activity: {
    coding: [
      {
        system: "http://terminology.hl7.org/CodeSystem/observation-category",
        code: "activity",
        display: "Activity",
      },
    ],
  } as CodeableConcept,

  BodyMeasurement: {
    coding: [
      {
        system: "http://terminology.hl7.org/CodeSystem/observation-category",
        code: "vital-signs",
        display: "Vital Signs",
      },
    ],
  } as CodeableConcept,
} as const;

//------------------------------------------------------------------------------
// FHIR Statistics Codes
//------------------------------------------------------------------------------

/**
 * FHIR Observation statistics codes for aggregate measurements.
 * Used in Observation.component to represent daily summary statistics.
 * @see https://www.hl7.org/fhir/valueset-observation-statistics.html
 */
export const ObservationStatistics = {
  /** Code system for observation statistics */
  System: "http://hl7.org/fhir/observation-statistics",

  /** Average/mean value */
  Average: {
    coding: [
      {
        system: "http://hl7.org/fhir/observation-statistics",
        code: "average",
        display: "Average",
      },
    ],
  } as CodeableConcept,

  /** Maximum value */
  Maximum: {
    coding: [
      {
        system: "http://hl7.org/fhir/observation-statistics",
        code: "max",
        display: "Maximum",
      },
    ],
  } as CodeableConcept,

  /** Minimum value */
  Minimum: {
    coding: [
      {
        system: "http://hl7.org/fhir/observation-statistics",
        code: "min",
        display: "Minimum",
      },
    ],
  } as CodeableConcept,

  /** Count of measurements */
  Count: {
    coding: [
      {
        system: "http://hl7.org/fhir/observation-statistics",
        code: "count",
        display: "Count",
      },
    ],
  } as CodeableConcept,

  /** Total count of observations in the period (not sum of values) */
  Total: {
    coding: [
      {
        system: "http://hl7.org/fhir/observation-statistics",
        code: "total-count",
        display: "Total Count",
      },
    ],
  } as CodeableConcept,
} as const;

/**
 * Sleep stage codes for sleep observation components.
 * Custom codes based on common sleep tracking terminology.
 */
export const SleepStages = {
  System: "http://vibrant.health/sleep-stages",

  REM: {
    coding: [
      {
        system: "http://vibrant.health/sleep-stages",
        code: "rem",
        display: "REM Sleep",
      },
    ],
  } as CodeableConcept,

  Deep: {
    coding: [
      {
        system: "http://vibrant.health/sleep-stages",
        code: "deep",
        display: "Deep Sleep",
      },
    ],
  } as CodeableConcept,

  Light: {
    coding: [
      {
        system: "http://vibrant.health/sleep-stages",
        code: "light",
        display: "Light Sleep",
      },
    ],
  } as CodeableConcept,

  Awake: {
    coding: [
      {
        system: "http://vibrant.health/sleep-stages",
        code: "awake",
        display: "Awake",
      },
    ],
  } as CodeableConcept,

  /**
   * Junction hypnogram value 5 ("manual") - a block the member logged by hand, so the time is real
   * sleep but the provider scored no stage for it. Only ever appears on the hypnogram timeline,
   * never as one of the aggregate stage totals.
   */
  Unspecified: {
    coding: [
      {
        system: "http://vibrant.health/sleep-stages",
        code: "unspecified",
        display: "Unspecified Sleep Stage",
      },
    ],
  } as CodeableConcept,
} as const;

/**
 * Codes describing the sleep period itself rather than how it decomposed into stages.
 *
 * These ride along as components on the daily Sleep Duration Observation so nothing Junction
 * reports about the night is discarded. `InBed` in particular is the denominator of sleep
 * efficiency and the input to the in-bed-minus-awake derivation, so storing it is what makes a
 * derived duration auditable after the fact.
 */
export const SleepPeriod = {
  System: "http://vibrant.health/sleep-period",

  /** Time in bed in minutes (Junction `duration`). NEVER the Sleep Duration value - see ENG-4411. */
  InBed: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "in-bed",
        display: "Time in Bed",
      },
    ],
  } as CodeableConcept,

  /** UTC instant the sleep period started (Junction `bedtime_start`). */
  BedtimeStart: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "bedtime-start",
        display: "Bedtime Start",
      },
    ],
  } as CodeableConcept,

  /** UTC instant the sleep period ended (Junction `bedtime_stop`). */
  BedtimeStop: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "bedtime-stop",
        display: "Bedtime Stop",
      },
    ],
  } as CodeableConcept,

  /** Junction `type`: long_sleep | short_sleep | acknowledged_nap | unknown. */
  Type: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "sleep-type",
        display: "Sleep Period Type",
      },
    ],
  } as CodeableConcept,

  /**
   * Junction `hr_dip`: percentage difference between average waking and average sleeping HR
   * (Garmin only). A heart-rate metric, but one defined entirely by the sleep period, so it rides
   * on the night's row rather than on the HeartRateObservation shared with activity data.
   */
  HeartRateDip: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "hr-dip",
        display: "Sleeping Heart Rate Dip",
      },
    ],
  } as CodeableConcept,

  /**
   * Junction `state`: whether the provider considers the summary final or still subject to change
   * (Garmin, EightSleep). Worth keeping - a TENTATIVE night explains a value that later moves.
   */
  State: {
    coding: [
      {
        system: "http://vibrant.health/sleep-period",
        code: "sleep-state",
        display: "Sleep Summary State",
      },
    ],
  } as CodeableConcept,
} as const;

/**
 * HRV metric codes for HRV observation components.
 * Based on standard HRV measurement methodologies.
 */
export const HRVMetrics = {
  System: "http://vibrant.health/hrv-metrics",

  SDNN: {
    coding: [
      {
        system: "http://vibrant.health/hrv-metrics",
        code: "sdnn",
        display: "SDNN (Standard Deviation of NN intervals)",
      },
    ],
  } as CodeableConcept,

  RMSSD: {
    coding: [
      {
        system: "http://vibrant.health/hrv-metrics",
        code: "rmssd",
        display: "RMSSD (Root Mean Square of Successive Differences)",
      },
    ],
  } as CodeableConcept,
} as const;

//------------------------------------------------------------------------------
// LOINC Code Definitions
//------------------------------------------------------------------------------

/**
 * Metadata for a LOINC code used in vital observations.
 */
export interface LOINCCodeDefinition {
  /** LOINC code (e.g., "55423-8") */
  code: string;
  /** Human-readable display name */
  display: string;
  /** Unit of measure (e.g., "kg", "%") */
  unit: string;
  /** UCUM unit code (e.g., "kg", "%", "{steps}") */
  unitCode: string;
  /** FHIR observation category */
  category: CodeableConcept;
}

/**
 * Centralized LOINC code definitions for all wearable/vital observations.
 *
 * Each entry includes:
 * - code: The LOINC code
 * - display: Human-readable name
 * - unit: Display unit
 * - unitCode: UCUM unit code
 * - category: FHIR observation category
 *
 * Sources:
 * - LOINC: https://loinc.org/
 * - UCUM: https://ucum.org/ucum
 */
export const VitalLOINCCodes = {
  //----------------------------------------------------------------------------
  // Activity Metrics
  //----------------------------------------------------------------------------

  /** Number of steps in 24 hours */
  Steps: {
    code: "55423-8",
    display: "Number of steps",
    unit: "steps",
    unitCode: "{steps}",
    category: ObservationCategories.Activity,
  },

  /** Active calories burned during physical activity */
  ActiveCalories: {
    code: "41981-2",
    display: "Calories burned in physical activity",
    unit: "kcal",
    unitCode: "kcal",
    category: ObservationCategories.Activity,
  },

  /** Basal/resting metabolic calories */
  BasalCalories: {
    code: "82259-3",
    display: "Basal calories burned",
    unit: "kcal",
    unitCode: "kcal",
    category: ObservationCategories.Activity,
  },

  /** Distance traveled */
  Distance: {
    code: "55430-3",
    display: "Distance",
    unit: "m",
    unitCode: "m",
    category: ObservationCategories.Activity,
  },

  /**
   * Floors/flights climbed in 24 hours
   * Note: 93833-2 = "Flights climbed 24 hour" (older code)
   * Alternative: 100304-5 (used by HealthKitOnFHIR)
   * We use 93833-2 for consistency with Vital/Junction ecosystem
   */
  FloorsClimbed: {
    code: "93833-2",
    display: "Floors climbed",
    unit: "floors",
    unitCode: "{floors}",
    category: ObservationCategories.Activity,
  },

  //----------------------------------------------------------------------------
  // Sleep Metrics
  //----------------------------------------------------------------------------

  /** Total sleep duration */
  SleepDuration: {
    code: "93832-4",
    display: "Sleep duration",
    unit: "min",
    unitCode: "min",
    category: ObservationCategories.Activity,
  },

  /** Sleep efficiency (time asleep / time in bed) - DISTINCT from FloorsClimbed */
  SleepEfficiency: {
    code: "93831-6",
    display: "Sleep efficiency",
    unit: "%",
    unitCode: "%",
    category: ObservationCategories.Activity,
  },

  //----------------------------------------------------------------------------
  // Cardiovascular Vitals
  //----------------------------------------------------------------------------

  /** Heart rate (general) */
  HeartRate: {
    code: "8867-4",
    display: "Heart rate",
    unit: "beats/min",
    unitCode: "/min",
    category: ObservationCategories.VitalSigns,
  },

  /** Resting heart rate */
  RestingHeartRate: {
    code: "40443-4",
    display: "Heart rate resting",
    unit: "bpm",
    unitCode: "/min",
    category: ObservationCategories.VitalSigns,
  },

  /** Heart rate variability (R-R interval variation) */
  HRV: {
    code: "80404-7",
    display: "Heart rate variability",
    unit: "ms",
    unitCode: "ms",
    category: ObservationCategories.VitalSigns,
  },

  /** Blood oxygen saturation (SpO2) */
  BloodOxygen: {
    code: "59408-5",
    display: "Oxygen saturation by pulse oximetry",
    unit: "%",
    unitCode: "%",
    category: ObservationCategories.VitalSigns,
  },

  /** Respiratory rate */
  RespiratoryRate: {
    code: "9279-1",
    display: "Respiratory rate",
    unit: "/min",
    unitCode: "/min",
    category: ObservationCategories.VitalSigns,
  },

  /** Systolic blood pressure */
  BloodPressureSystolic: {
    code: "8480-6",
    display: "Systolic blood pressure",
    unit: "mmHg",
    unitCode: "mm[Hg]",
    category: ObservationCategories.VitalSigns,
  },

  /** Diastolic blood pressure */
  BloodPressureDiastolic: {
    code: "8462-4",
    display: "Diastolic blood pressure",
    unit: "mmHg",
    unitCode: "mm[Hg]",
    category: ObservationCategories.VitalSigns,
  },

  /** VO2 max (maximal oxygen consumption) */
  VO2Max: {
    code: "60842-2",
    display: "VO2 max",
    unit: "mL/kg/min",
    unitCode: "mL/kg/min",
    category: ObservationCategories.VitalSigns,
  },

  //----------------------------------------------------------------------------
  // Body Composition Metrics
  //----------------------------------------------------------------------------

  /** Body height */
  BodyHeight: {
    code: "8302-2",
    display: "Body height",
    unit: "in",
    unitCode: "[in_i]",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Body weight */
  BodyWeight: {
    code: "29463-7",
    display: "Body weight",
    unit: "kg",
    unitCode: "kg",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Body mass index */
  BMI: {
    code: "39156-5",
    display: "Body mass index (BMI)",
    unit: "kg/m2",
    unitCode: "kg/m2",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Body fat percentage */
  BodyFat: {
    code: "41982-0",
    display: "Body fat percentage",
    unit: "%",
    unitCode: "%",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Fat mass (absolute) */
  FatMass: {
    code: "73708-0",
    display: "Body fat mass",
    unit: "kg",
    unitCode: "kg",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Lean body mass */
  LeanBodyMass: {
    code: "88334-8",
    display: "Lean body mass",
    unit: "kg",
    unitCode: "kg",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Skeletal muscle mass */
  MuscleMass: {
    code: "73964-9",
    display: "Skeletal muscle mass",
    unit: "kg",
    unitCode: "kg",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Body water percentage */
  BodyWaterPercentage: {
    code: "73706-4",
    display: "Body water percentage",
    unit: "%",
    unitCode: "%",
    category: ObservationCategories.BodyMeasurement,
  },

  /** Bone mineral content/mass */
  BoneMass: {
    code: "38265-5",
    display: "Bone mineral content",
    unit: "kg",
    unitCode: "kg",
    category: ObservationCategories.BodyMeasurement,
  },

  //----------------------------------------------------------------------------
  // Glucose & Diabetes Metrics
  //----------------------------------------------------------------------------

  /**
   * Glucose in interstitial fluid (CGM) - individual readings
   * Use 99504-3 for individual point-in-time readings from CGM devices.
   * Use 97507-8 for averaged readings over a reporting period.
   */
  GlucoseInterstitial: {
    code: "99504-3",
    display: "Glucose [Mass/volume] in Interstitial fluid",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: ObservationCategories.VitalSigns,
  },

  /** Glucose in blood (standard blood glucose) */
  GlucoseBlood: {
    code: "2339-0",
    display: "Glucose [Mass/volume] in Blood",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: ObservationCategories.VitalSigns,
  },

  /**
   * Insulin dose administered
   * LOINC 20443-9: "Insulin dose"
   * Used for recording insulin dosage from pumps, CGM systems, or manual entry.
   * Note: 93846-4 is incorrect (Hemoglobin [Moles/volume] in Venous blood)
   */
  InsulinDose: {
    code: "20443-9",
    display: "Insulin dose",
    unit: "U",
    unitCode: "[iU]",
    category: ObservationCategories.VitalSigns,
  },

  //----------------------------------------------------------------------------
  // Nutrition Metrics
  //----------------------------------------------------------------------------

  /** Carbohydrate intake */
  CarbohydrateIntake: {
    code: "9059-7",
    display: "Carbohydrate intake",
    unit: "g",
    unitCode: "g",
    category: ObservationCategories.Activity,
  },

  /** Water intake */
  WaterIntake: {
    code: "99127-2",
    display: "Water intake",
    unit: "mL",
    unitCode: "mL",
    category: ObservationCategories.Activity,
  },

  /** Caffeine intake */
  CaffeineIntake: {
    code: "42719-5",
    display: "Caffeine intake",
    unit: "mg",
    unitCode: "mg",
    category: ObservationCategories.Activity,
  },

  //----------------------------------------------------------------------------
  // Body Temperature
  //----------------------------------------------------------------------------

  /** Body temperature */
  BodyTemperature: {
    code: "8310-5",
    display: "Body temperature",
    unit: "°C",
    unitCode: "Cel",
    category: ObservationCategories.VitalSigns,
  },
} as const;

/**
 * Type for the keys of VitalLOINCCodes for type-safe access.
 */
export type VitalLOINCCodeKey = keyof typeof VitalLOINCCodes;

/**
 * Legacy WearableLOINCCodes for backwards compatibility.
 * @deprecated Use VitalLOINCCodes instead, which includes full metadata.
 */
export const WearableLOINCCodes = {
  // Activity
  Steps: VitalLOINCCodes.Steps.code,
  ActiveCalories: VitalLOINCCodes.ActiveCalories.code,
  BasalCalories: VitalLOINCCodes.BasalCalories.code,
  Distance: VitalLOINCCodes.Distance.code,
  FloorsClimbed: VitalLOINCCodes.FloorsClimbed.code,

  // Sleep
  SleepDuration: VitalLOINCCodes.SleepDuration.code,
  SleepEfficiency: VitalLOINCCodes.SleepEfficiency.code,

  // Vitals
  HeartRate: VitalLOINCCodes.HeartRate.code,
  RestingHeartRate: VitalLOINCCodes.RestingHeartRate.code,
  HRV: VitalLOINCCodes.HRV.code,
  BloodOxygen: VitalLOINCCodes.BloodOxygen.code,
  RespiratoryRate: VitalLOINCCodes.RespiratoryRate.code,

  // Body
  BodyWeight: VitalLOINCCodes.BodyWeight.code,
  BMI: VitalLOINCCodes.BMI.code,
  BodyFat: VitalLOINCCodes.BodyFat.code,
  BodyTemperature: VitalLOINCCodes.BodyTemperature.code,

  // Glucose
  GlucoseInterstitial: VitalLOINCCodes.GlucoseInterstitial.code,
  GlucoseBlood: VitalLOINCCodes.GlucoseBlood.code,

  // Nutrition
  CarbohydrateIntake: VitalLOINCCodes.CarbohydrateIntake.code,

  // Insulin
  InsulinDose: VitalLOINCCodes.InsulinDose.code,
} as const;
